/* eslint-disable solid/no-innerhtml */
import {
  Component,
  createEffect,
  createResource,
  createSignal,
  For,
  onMount,
  Show,
  Suspense,
} from 'solid-js'

import { LoadingSpinner } from '@/components/LegacyIcons'
import {
  wpQuery,
  wpQueryApiBase,
  type companyTaxonomy,
  type companyType,
} from '@/lib/api/bapFormQuery'
import { cx } from '@/utils/utils'
import { Company } from '@/web-components/BapFormApp/BapCompany'
import { FormBapByCompany } from '@/web-components/BapFormApp/FormBapByCompany'
import { FormCompanyByCategory } from '@/web-components/BapFormApp/FormCompanyByCategory'
import ky from 'ky'
import queryString from 'query-string'
import { getCurrentElement } from 'solid-element'
import { z } from 'zod'
import { TermSchema } from './BapFormSchema'

async function fetchCompanies(companyType: companyType) {
  try {
    const data = await wpQuery.getCompanies({
      query: {
        meta_key: companyType,
        meta_value: true,
        orderby: 'title',
        order: 'asc',
      },
    })
    // console.log(data)
    return data
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
  }
}

const fetchAllBapCategories = async () => {
  try {
    const allCategories: z.infer<typeof TermSchema>[] = []
    let page = 1
    const perPage = 100
    let hasMorePages = true

    while (hasMorePages) {
      const requestUrl = queryString.stringifyUrl({
        url: wpQueryApiBase + '/bap_category',
        query: {
          per_page: perPage,
          page: page,
          orderby: 'name',
          order: 'asc',
        },
      })

      const response = await ky.get(requestUrl).json()
      const parsed = z.array(TermSchema).parse(response)

      allCategories.push(...parsed)

      // If we got fewer results than the page size, we've reached the end
      if (parsed.length < perPage) {
        hasMorePages = false
      } else {
        page++
      }
    }

    return allCategories
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
    return []
  }
}

export type formTypes = 'company' | 'category'
type FormAppProps = {
  companyType: companyType
  companyTaxonomy: companyTaxonomy
}
type updateHandlerProps = { companies: number[]; form: formTypes }
export type updateHandler = (props: updateHandlerProps) => void
// function updateHandler(companies: number[], companyType: companyType) {
//   setCompanyIds(companies)
// }

export const BapFormApp: Component<FormAppProps> = (props) => {
  const [companyIds, setCompanyIds] = createSignal<number[] | undefined>()
  const [companyType, setCompanyType] = createSignal<companyType>()
  const [companyTaxonomy, setCompanyTaxonomy] = createSignal<companyTaxonomy>()
  const [companies] = createResource(companyType, fetchCompanies)
  const [categories] = createResource(companyTaxonomy, fetchAllBapCategories)
  const [activeForm, setActiveForm] = createSignal<formTypes>()

  const updateHandler: updateHandler = (params) => {
    setCompanyIds(params.companies)
    setActiveForm(params.form)
  }

  onMount(() => {
    getCurrentElement().classList.add(cx('block'))
  })

  createEffect(() => {
    setCompanyType(props.companyType)
    setCompanyTaxonomy(props.companyTaxonomy)
  })

  return (
    <Suspense
      fallback={
        <div class="relative min-h-[9rem]">
          <div class="absolute inset-0 flex items-center justify-center heir-svg:h-80 heir-svg:w-80">
            <LoadingSpinner />
          </div>
        </div>
      }
    >
      <div class="">
        <div class="mb-32 space-y-30">
          <Show when={companies()}>
            <FormBapByCompany
              companies={companies()}
              updateHandler={updateHandler}
              activeForm={activeForm()}
            />
          </Show>
          <Show when={categories()} keyed>
            {(cat) => (
              <FormCompanyByCategory
                companyTaxonomy={props.companyTaxonomy}
                terms={cat}
                updateHandler={updateHandler}
                activeForm={activeForm()}
              />
            )}
          </Show>
        </div>
        <div class="divide-y-1 not-prose relative divide-blue rounded-20px bg-legacy-paleBlue px-24 md:px-30">
          <Show when={categories.state === 'ready' && categories()} keyed>
            {(cats) => (
              <Show when={companyIds()} keyed>
                {(companies) => (
                  <For each={companies}>
                    {(company) => (
                      <Company
                        class="pt-30 last:pb-30"
                        companyType={props.companyType}
                        companyId={company}
                        categories={cats}
                      />
                    )}
                  </For>
                )}
              </Show>
            )}
          </Show>
        </div>
      </div>
    </Suspense>
  )
}
